/**
 * Recovering from a sign-in redirect that cannot be completed (#475).
 *
 * ── THE THIRD ATTEMPT, AND WHY THE FIRST TWO MISSED ──
 *
 * Attempt 1 set navigateToLoginRequestUrl: false. Attempt 2 stopped
 * main.jsx rendering before handleRedirectPromise resolved. Both were
 * about ORDERING, both were reasonable, and the double sign-in survived
 * both -- because the ordering was never the whole story.
 *
 * The device report that settled it carries the actual error:
 *
 *     main.jsx:76 Auth redirect could not be completed
 *     BrowserAuthError: no_cached_authority_error: No cached authority found.
 *         at j9.handleResponse (RedirectClient.mjs:264:19)
 *         at j9.handleRedirectPromise (RedirectClient.mjs:138:35)
 *
 * So by the time of attempt 2 the redirect WAS being handled at the right
 * moment. It throws. main.jsx caught it and called start() anyway, the app
 * came up signed out, and the user -- who had just typed a correct
 * password -- was handed the sign-in button back. Same symptom as attempts
 * 1 and 2 were chasing, arriving down a completely different road, which
 * is why fixing the ordering twice never moved it.
 *
 * ── WHAT MSAL IS ACTUALLY COMPLAINING ABOUT ──
 *
 * Read out of @azure/msal-browser 3.30.0 rather than inferred.
 * RedirectClient.handleResponse does this before it can redeem the code:
 *
 *     const currentAuthority = this.browserStorage.getCachedAuthority(state);
 *     if (!currentAuthority) {
 *         throw createBrowserAuthError(noCachedAuthorityError);
 *     }
 *
 * loginRedirect writes that authority into the TEMPORARY cache on the way
 * out, keyed by the request's `state`, and handleResponse reads it back on
 * the way in. It is the one piece of the round trip that lives in the
 * browser rather than in the URL, so it is the one piece that can go
 * missing while the response itself arrives intact.
 *
 * BrowserCacheManager.getTemporaryCache decides where to look:
 *
 *     if (this.cacheConfig.storeAuthStateInCookie) {
 *         const itemCookie = this.cookieStorage.getItem(key);
 *         if (itemCookie) return itemCookie;
 *     }
 *     const value = this.temporaryCacheStorage.getItem(key);
 *
 * With storeAuthStateInCookie false -- which is what this app shipped --
 * there is NO first branch. The authority exists only in sessionStorage,
 * and sessionStorage is the storage with the weakest guarantee of
 * surviving a navigation out to an identity provider and back. Hence the
 * cookie fallback now set in msalConfig.js, which is the primary fix and
 * removes the dependency entirely.
 *
 * ── THIS MODULE IS THE SECOND HALF, AND IT EARNS ITS PLACE ──
 *
 * The cookie fallback should stop the error happening. This handles it
 * happening anyway, and is worth having on its own merits: a redirect that
 * cannot be completed is RECOVERABLE, because the identity provider's own
 * session now exists. That is precisely why clicking Sign in a second time
 * walks straight in with no password -- the user is already authenticated
 * upstream, and all that failed was our half of the handshake.
 *
 * So the second click is work the app can do itself. Doing it
 * automatically is the difference between "I have to sign in twice" and no
 * symptom at all.
 *
 * ── WHAT MUST NOT BE RETRIED ──
 *
 * A cancelled sign-in, a policy the user backed out of, a denied consent.
 * Sending those back to the identity provider is a redirect loop that the
 * user cannot escape without clearing site data -- far worse than the bug
 * being fixed. Only the lost-temporary-state family below is retried, and
 * anything unrecognised renders signed out exactly as before.
 */

/**
 * The failures that mean "our temporary state is gone", as distinct from
 * "the user said no". Every one of these is thrown AFTER the identity
 * provider has already authenticated somebody.
 *
 * Codes taken from msal-browser's BrowserAuthErrorCodes rather than
 * typed from memory.
 */
export const RECOVERABLE_REDIRECT_ERRORS = Object.freeze([
  // The authority for this request's state is not in the temporary cache.
  // The reported failure.
  "no_cached_authority_error",
  // The request parameters themselves are gone, same cause one step earlier.
  "no_token_request_cache_error",
  // They are present but unreadable, which is a lost-state failure too.
  "unable_to_parse_token_request_cache_error",
  // The state in the hash matches nothing we stored.
  "state_not_found",
  "invalid_state",
]);

const RETRY_MARKER_KEY = "beekeeper.signin_retry_at";

/**
 * How long a retry marker suppresses another one. Long enough that a
 * redirect loop cannot form (one IdP round trip is seconds), short enough
 * that a genuine sign-in attempt an hour later is not refused a recovery
 * it deserves.
 */
export const RETRY_SUPPRESSION_MS = 60_000;

/**
 * Is this the kind of redirect failure a fresh sign-in would fix?
 *
 * Matches on errorCode, and falls back to the message because a
 * BrowserAuthError stringifies as "<code>: <description>" and some
 * wrappers hand on only the message.
 */
export function isRecoverableRedirectFailure(err) {
  if (!err) return false;
  const code = typeof err.errorCode === "string" ? err.errorCode : "";
  if (RECOVERABLE_REDIRECT_ERRORS.includes(code)) return true;

  const message = typeof err.message === "string" ? err.message : "";
  return RECOVERABLE_REDIRECT_ERRORS.some((c) => message.includes(c));
}

/**
 * ONE retry, not a loop.
 *
 * THE MARKER LIVES IN localStorage, DELIBERATELY. sessionStorage is the
 * storage whose unreliability caused this bug; a guard kept there could
 * vanish in exactly the circumstances that trigger the retry, and a guard
 * that can vanish is not a guard. localStorage also survives the redirect
 * to the identity provider, which is the whole point -- the marker is set
 * before navigating away and read after coming back.
 *
 * Storage can be blocked outright (private windows, strict settings). In
 * that case shouldRetrySignIn returns false rather than throwing: no
 * recovery is a worse experience, an unguarded redirect loop is a broken
 * app, and the user can still click Sign in themselves.
 */
export function shouldRetrySignIn(now = Date.now(), storage = safeLocalStorage()) {
  if (!storage) return false;
  let previous;
  try {
    previous = storage.getItem(RETRY_MARKER_KEY);
  } catch {
    return false;
  }
  if (!previous) return true;

  const at = Number(previous);
  // An unparseable or future-dated marker is treated as recent, which
  // fails toward not looping.
  if (!Number.isFinite(at)) return false;
  return now - at > RETRY_SUPPRESSION_MS;
}

/** Record that a retry is being attempted now. Must happen BEFORE the
 *  redirect, because after it there is no "after". */
export function markSignInRetry(now = Date.now(), storage = safeLocalStorage()) {
  if (!storage) return;
  try {
    storage.setItem(RETRY_MARKER_KEY, String(now));
  } catch {
    /* A blocked or full storage must never stop the sign-in. */
  }
}

/** Clear the marker once a sign-in has actually succeeded, so a later
 *  failure in a long-lived session still gets its one recovery. */
export function clearSignInRetry(storage = safeLocalStorage()) {
  if (!storage) return;
  try {
    storage.removeItem(RETRY_MARKER_KEY);
  } catch {
    /* ignored for the same reason as above */
  }
}

function safeLocalStorage() {
  try {
    return typeof localStorage === "undefined" ? null : localStorage;
  } catch {
    // Accessing the property itself throws when site data is blocked.
    return null;
  }
}
